import { useCallback, useEffect, useRef } from 'react';
import { useFormResponseStore } from '@/store/form/use-form-response-store';

const STORAGE_PREFIX = 'uzaforms:draft';
const WRITE_DEBOUNCE_MS = 600;

interface StoredDraft {
  savedAt: string;
  formResponses: Record<string, any>;
}

interface UseFormDraftStorageArgs {
  userId?: string;
  processId?: string;
  formId?: string;
  /** Absent while the applicant is filling the very first form of a new application. */
  applicantProcessId?: string;
  /** Hold off until the form's sections exist, so we never hydrate into an empty form. */
  enabled?: boolean;
}

export const buildDraftKey = ({
  userId,
  processId,
  formId,
  applicantProcessId,
}: UseFormDraftStorageArgs) =>
  `${STORAGE_PREFIX}:${userId ?? 'anon'}:${processId ?? 'na'}:${formId ?? 'na'}:${
    applicantProcessId ?? 'new'
  }`;

const readDraft = (key: string): StoredDraft | null => {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredDraft;
    if (!parsed?.formResponses || typeof parsed.formResponses !== 'object') return null;
    return parsed;
  } catch {
    // Private mode, corrupt JSON, or storage disabled — behave as if there is no draft
    return null;
  }
};

/**
 * Keeps the in-progress answers of a single form mirrored into localStorage so an
 * accidental reload, crash or power cut does not lose them, and restores them on
 * the next mount. Scoped per user, process, form and application, so drafts for
 * different forms never overwrite one another.
 */
export const useFormDraftStorage = ({
  userId,
  processId,
  formId,
  applicantProcessId,
  enabled = true,
}: UseFormDraftStorageArgs) => {
  const { formResponses, hydrateResponses } = useFormResponseStore();
  const key = buildDraftKey({ userId, processId, formId, applicantProcessId });
  const restoredKey = useRef<string | null>(null);
  const writeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearDraft = useCallback(() => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // nothing to do — the draft simply outlives this session
    }
  }, [key]);

  // Restore once per form. Keyed on `key` rather than a plain boolean so that
  // switching forms without remounting still restores the new form's draft.
  useEffect(() => {
    if (!enabled || !formId || restoredKey.current === key) return;
    restoredKey.current = key;

    const stored = readDraft(key);
    if (stored && Object.keys(stored.formResponses).length > 0) {
      hydrateResponses(stored.formResponses);
    }
  }, [enabled, formId, key, hydrateResponses]);

  // Mirror subsequent edits, debounced so typing does not thrash storage
  useEffect(() => {
    if (!enabled || restoredKey.current !== key || !formId) return;

    if (writeTimer.current) clearTimeout(writeTimer.current);
    writeTimer.current = setTimeout(() => {
      try {
        // An empty store means the form was reset or unmounted, not that the user
        // erased their work. Leave any saved draft alone; only an explicit
        // clearDraft() on successful submit removes it.
        if (Object.keys(formResponses).length === 0) return;
        const payload: StoredDraft = {
          savedAt: new Date().toISOString(),
          formResponses,
        };
        window.localStorage.setItem(key, JSON.stringify(payload));
      } catch {
        // Quota exceeded or storage unavailable — drafting is best-effort
      }
    }, WRITE_DEBOUNCE_MS);

    return () => {
      if (writeTimer.current) clearTimeout(writeTimer.current);
    };
  }, [enabled, formId, key, formResponses]);

  return { clearDraft };
};
